import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TextField, SelectField, AreaField } from "@/components/common";
import { PROPERTY_TYPE, OCCUPANCY, CONDITION, CONTACT_METHOD } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";
import logo from "@/assets/galahadji-logo.png";

export default function SellerIntake() {
  const [f, setF] = useState({ preferred_contact_method: "Phone", property_type: "Single Family", occupancy_status: "Owner Occupied", property_condition: "Average", foreclosure_involved: false, authorized_to_sell: false });
  const [done, setDone] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/seller-intake", f);
      setDone(data.lead_id_display);
      toast.success("Submission received!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-primary text-primary-foreground flex items-center justify-center grain p-6">
      <div className="text-center max-w-lg relative z-10" data-testid="intake-success">
        <CheckCircle2 className="w-16 h-16 text-accent mx-auto" />
        <h1 className="font-marketing text-4xl font-bold mt-6">Thank you.</h1>
        <p className="text-white/70 mt-4">Your property has been submitted (Ref <span className="text-accent font-mono-nums">{done}</span>). A Galahadji acquisition manager will reach out within one business day.</p>
        <Link to="/" className="inline-block mt-8 bg-accent text-accent-foreground px-6 py-3 rounded-md font-medium hover:bg-accent/90 transition-colors">Back to Home</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border backdrop-blur-xl bg-white/70 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="seller-header-logo" className="flex items-center gap-2"><img src={logo} alt="Galahadji Acquisitions" className="h-14 w-auto object-contain" /></Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Team Login</Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <span className="text-accent uppercase tracking-[0.25em] text-xs font-medium">Seller Intake</span>
        <h1 className="font-marketing text-4xl sm:text-5xl font-bold tracking-tight mt-3">Tell us about your property.</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">The more you share, the faster we can prepare a tailored offer. Everything is confidential.</p>

        <form onSubmit={submit} className="mt-10 space-y-10" data-testid="seller-intake-form">
          <section>
            <h2 className="font-display text-lg font-bold mb-4 pb-2 border-b border-border">Your Details</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="First Name" value={f.first_name} onChange={set("first_name")} testid="intake-first-name" />
              <TextField label="Last Name" value={f.last_name} onChange={set("last_name")} testid="intake-last-name" />
              <TextField label="Phone" value={f.phone} onChange={set("phone")} testid="intake-phone" />
              <TextField label="Email" type="email" value={f.email} onChange={set("email")} testid="intake-email" />
              <SelectField label="Preferred Contact" value={f.preferred_contact_method} onChange={set("preferred_contact_method")} options={CONTACT_METHOD} testid="intake-contact-method" />
              <TextField label="Best Contact Time" value={f.best_contact_time} onChange={set("best_contact_time")} testid="intake-contact-time" />
            </div>
          </section>

          <section>
            <h2 className="font-display text-lg font-bold mb-4 pb-2 border-b border-border">The Property</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="Property Address" value={f.property_address} onChange={set("property_address")} testid="intake-address" className="sm:col-span-2" />
              <TextField label="City" value={f.city} onChange={set("city")} testid="intake-city" />
              <TextField label="State" value={f.state} onChange={set("state")} testid="intake-state" />
              <TextField label="ZIP Code" value={f.zip_code} onChange={set("zip_code")} testid="intake-zip" />
              <TextField label="County" value={f.county} onChange={set("county")} testid="intake-county" />
              <SelectField label="Property Type" value={f.property_type} onChange={set("property_type")} options={PROPERTY_TYPE} testid="intake-type" />
              <SelectField label="Occupancy" value={f.occupancy_status} onChange={set("occupancy_status")} options={OCCUPANCY} testid="intake-occupancy" />
              <TextField label="Bedrooms" type="number" value={f.bedrooms} onChange={set("bedrooms")} testid="intake-beds" />
              <TextField label="Bathrooms" type="number" value={f.bathrooms} onChange={set("bathrooms")} testid="intake-baths" />
              <TextField label="Square Feet" type="number" value={f.square_feet} onChange={set("square_feet")} testid="intake-sqft" />
              <TextField label="Year Built" type="number" value={f.year_built} onChange={set("year_built")} testid="intake-year" />
              <SelectField label="Condition" value={f.property_condition} onChange={set("property_condition")} options={CONDITION} testid="intake-condition" />
            </div>
          </section>

          <section>
            <h2 className="font-display text-lg font-bold mb-4 pb-2 border-b border-border">Your Situation</h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <TextField label="Asking Price ($)" type="number" value={f.asking_price} onChange={set("asking_price")} testid="intake-asking" />
              <TextField label="Desired Closing Date" type="date" value={f.desired_closing_date} onChange={set("desired_closing_date")} testid="intake-closing" />
              <TextField label="Estimated Market Value ($)" type="number" value={f.estimated_market_value} onChange={set("estimated_market_value")} testid="intake-value" />
              <div className="sm:col-span-2"><AreaField label="What's motivating your sale?" value={f.seller_motivation} onChange={set("seller_motivation")} testid="intake-motivation" /></div>
              <div className="flex items-center gap-3 pt-2">
                <Checkbox id="fc" checked={f.foreclosure_involved} onCheckedChange={set("foreclosure_involved")} data-testid="intake-foreclosure" />
                <Label htmlFor="fc" className="text-sm">This property is in foreclosure</Label>
              </div>
              <div className="flex items-center gap-3 pt-2">
                <Checkbox id="auth" checked={f.authorized_to_sell} onCheckedChange={set("authorized_to_sell")} data-testid="intake-authorized" />
                <Label htmlFor="auth" className="text-sm">I am authorized to sell this property</Label>
              </div>
            </div>
          </section>

          <Button type="submit" disabled={loading} data-testid="intake-submit" className="h-12 px-10 text-base">{loading ? "Submitting…" : "Submit Property"}</Button>
        </form>
      </div>
    </div>
  );
}
